import React, { useState, useRef, useEffect } from "react";
import AxiosInstance from "../../../utils/AxiosInstance";
import { useNetwork } from "../../../context/NetworkContext";
import { useShowAlert } from "../../../context/AlertContext";
import handleError from "../../../constants/handleError";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import { CloseIcon } from "../../../constants/Icons";

const TaskModal = ({
  jobcardId,
  ensureJobcard,
  onClose,
  onTaskSaved,
  editTask,
  titleSuggest,
  localTitleSuggest,
  setLocalTitleSuggest,
}) => {
  const { checkNetwork } = useNetwork();
  const showAlert = useShowAlert();
  const [allSuggestions, setAllSuggestions] = useState([]);
  const [showTitlePopup, setShowTitlePopup] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const suggestionRef = useRef([]);
  const [error, setError] = useState({ title: "" });
  const suggestionsContainerRef = useRef(null);

  const [newTask, setNewTask] = useState({
    title: editTask?.title || "",
    description: editTask?.description || "",
    amount: editTask?.amount
      ? CurrencyConverter.toCurrency(editTask.amount, "INR")
      : "",
    status: editTask?.status || "PENDING",
  });

  const descRef = useRef(null);
  const amountRef = useRef(null);

  const getCombinedSuggestions = () => {
    const combined = [...localTitleSuggest, ...titleSuggest];
    const seen = new Set();
    return combined.filter((t) => {
      if (seen.has(t.title)) return false;
      seen.add(t.title);
      return true;
    });
  };

  const handleSave = async () => {
    const trimmedTitle = newTask.title.trim();
    // const trimmedAmount = newTask.amount.trim();
    setError({ title: "" });
    if (!trimmedTitle) {
      setError({ ...error, title: "Title is required" });
      return;
    }

    // if (!trimmedAmount || isNaN(trimmedAmount)) {
    //   setError({ ...error, amount: "Amount is required and must be a number" });
    //   return;
    // }

    try {
      if (!checkNetwork()) return;

      const id = jobcardId || (await ensureJobcard());

      const payload = {
        title: newTask.title,
        description: newTask.description || "",
        amount: newTask.amount
          ? CurrencyConverter.toSubunit(newTask.amount, "INR")
          : 0,
        status: newTask.status.toUpperCase(),
      };

      let response;
      if (editTask?._id) {
        response = await AxiosInstance.patch(`module/jobcard/task/${id}`, {
          taskid: editTask._id,
          ...payload,
        });
      } else {
        response = await AxiosInstance.post(`module/jobcard/task/${id}`, {
          task: [payload],
        });
        const exists = localTitleSuggest.some((t) => t.title === newTask.title);
        if (!exists) {
          const newTitleObj = {
            title: newTask.title,
            _id: Date.now().toString(),
            usedAt: new Date().toISOString(),
            usageCount: 1,
            score: 1,
          };
          setLocalTitleSuggest([newTitleObj, ...localTitleSuggest]);
        }
      }

      const updatedJobcard = response?.data?.results;
      if (updatedJobcard) {
        onTaskSaved(updatedJobcard);
        onClose();
      }
    } catch (error) {
      handleError(error, showAlert);
    }
  };

  const handleSearchTitle = (value) => {
    const combined = getCombinedSuggestions();
    const filtered = value
      ? combined.filter((t) =>
          t.title.toLowerCase().includes(value.toLowerCase()),
        )
      : combined;

    const seen = new Set();
    const uniqueFiltered = filtered.filter((t) => {
      if (seen.has(t.title)) return false;
      seen.add(t.title);
      return true;
    });

    setAllSuggestions(
      uniqueFiltered.map((t) => ({ title: t.title, source: "local" })),
    );
    setShowTitlePopup(true);
    setHighlightedIndex(0);
  };

  const selectSuggestion = (suggestion) => {
    setNewTask((prev) => ({ ...prev, title: suggestion.title }));
    setShowTitlePopup(false);
    setHighlightedIndex(-1);
  };

  const handleTitleKeyDown = (e) => {
    if (showTitlePopup && allSuggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < allSuggestions.length - 1 ? prev + 1 : 0,
        );
        return;
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : allSuggestions.length - 1,
        );
        return;
      } else if (e.key === "Enter") {
        if (highlightedIndex >= 0) {
          e.preventDefault();
          selectSuggestion(allSuggestions[highlightedIndex]);
          return;
        }
      } else if (e.key === "Escape") {
        setShowTitlePopup(false);
        return;
      }
    }
    if (e.key === "Enter" && newTask.title.trim()) {
      e.preventDefault();
      descRef.current?.focus();
      setShowTitlePopup(false);
    } else if (e.key === "Enter") {
      e.preventDefault();
      setError({ ...error, title: "Title is required" });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // If click is outside the suggestions container, close popup
      if (
        suggestionsContainerRef.current &&
        !suggestionsContainerRef.current.contains(event.target)
      ) {
        setShowTitlePopup(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div>
      <div className="modal-backdrop show"></div>
      <div className="modal show" style={{ display: "block" }}>
        <div className="modal-dialog modal-md modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {editTask ? "Edit Task" : "Add Task"}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
              ></button>
            </div>

            <div className="modal-body">
              {/* Title */}
              <div className="mb-3 position-relative">
                <label className="form-label">Title</label>
                <input
                  type="text"
                  className={`form-control`}
                  value={newTask.title}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewTask({ ...newTask, title: value });
                    setError("");

                    if (value.trim().length > 0) {
                      handleSearchTitle(value);
                    } else {
                      setAllSuggestions([]);
                      setShowTitlePopup(false);
                    }
                  }}
                  onFocus={() => {
                    const combinedSuggest = getCombinedSuggestions();
                    if (combinedSuggest.length > 0) {
                      setAllSuggestions(
                        combinedSuggest.map((r) => ({
                          title: r.title,
                          source: "local",
                        })),
                      );
                      setShowTitlePopup(true);
                      setHighlightedIndex(-1);
                    }
                  }}
                  onKeyDown={(e) => handleTitleKeyDown(e)}
                  autoFocus
                />
                {error.title && (
                  <div className="invalid-feedback">{error.title}</div>
                )}
                {showTitlePopup && allSuggestions.length > 0 && (
                  <ul
                    ref={suggestionsContainerRef}
                    className="suggestions-list"
                    style={{ maxHeight: "150px" }}
                  >
                    {allSuggestions.map((suggestion, index) => (
                      <li
                        key={index}
                        ref={(el) => (suggestionRef.current[index] = el)}
                        className={`suggestion-item ${
                          highlightedIndex === index ? "highlighted" : ""
                        }`}
                        onMouseDown={() => selectSuggestion(suggestion)}
                      >
                        {suggestion.title}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Description */}
              <div className="mb-3">
                <label className="form-label">Notes</label>{" "}
                <span className="text-muted fst-italic small">(Optional)</span>
                <textarea
                  ref={descRef}
                  rows={3}
                  className="form-control"
                  value={newTask.description}
                  onChange={(e) =>
                    setNewTask({ ...newTask, description: e.target.value })
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      amountRef.current?.focus();
                    }
                  }}
                ></textarea>
              </div>

              {/* Amount */}
              <div className="mb-3">
                <label className="form-label">Estimate Amount</label>{" "}
                <span className="text-muted fst-italic small">(Optional)</span>
                <div className="col-lg-6">
                  <input
                    ref={amountRef}
                    className="form-control"
                    value={newTask.amount}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^\d*$/.test(val)) {
                        setNewTask({ ...newTask, amount: val });
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSave(); // allow saving even if amount is empty
                      }
                    }}
                  />
                  {/* {error.amount && (
                  <div className="invalid-feedback">{error.amount}</div>
                )} */}
                </div>
              </div>
            </div>

            <div className="modal-footer d-flex justify-content-between">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={onClose}
              >
                C Cancel
              </button>
              <button
                type="button"
                className="btn"
                style={{ background: "#ffab1d", color: "#000", border: "none" }}
                onClick={handleSave}
              >
                <i
                  className={`me-1 ${
                    editTask ? "bx bx-check" : "ng ng-ng-add-icon"
                  }`}
                ></i>{" "}
                {editTask ? "Update" : "Add"} Task
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskModal;
